// Lightweight event tracker. Fire-and-forget POST to /api/track for
// analytics events the admin dashboard slices/dices.
//
// Design notes:
//  * Session ID is generated once per browser tab and stored in sessionStorage.
//    Distinct visits = distinct session ids. We don't need cookies or
//    persistent ids — the admin asked for "real-ish" funnels, not deduped
//    cross-visit users.
//  * All errors are swallowed: tracking must never block UX.
//  * If the navigator goes offline or the call fails, the event is dropped.
//    We do NOT queue/retry — a perfect funnel isn't worth the complexity.
//  * sendBeacon-style send for unloads isn't needed since we ping eagerly
//    on navigation.

import { apiFetch } from './client'

const SESSION_KEY = 'nimvoy.sid'
const FIRST_TOUCH_KEY = 'nimvoy.ft.v2'
const DEVICE_MODEL_KEY = 'nimvoy.dm'

// Device model via Client Hints. Chrome's reduced User-Agent hides the Android
// model ("Android 10; K"), so market-level eSIM-capability guesses were the
// best we could do. The model is still available ON REQUEST via
// getHighEntropyValues — ask once per tab, cache, and ride it on every track
// payload. iPhones never expose a model this way (fine: all iPhones since the
// XS support eSIM, "iPhone" in the UA is already the answer); Safari/Firefox
// don't implement the API and simply stay null.
let deviceModel: string | null = null

function initDeviceModel(): void {
  if (typeof window === 'undefined' || deviceModel) return
  try {
    const cached = sessionStorage.getItem(DEVICE_MODEL_KEY)
    if (cached) {
      deviceModel = cached
      return
    }
    const uaData = (navigator as Navigator & {
      userAgentData?: { getHighEntropyValues?: (hints: string[]) => Promise<{ model?: string }> }
    }).userAgentData
    uaData?.getHighEntropyValues?.(['model'])
      .then((r) => {
        const m = (r?.model || '').trim()
        if (m) {
          deviceModel = m.slice(0, 100)
          try { sessionStorage.setItem(DEVICE_MODEL_KEY, deviceModel) } catch { /* ignore */ }
        }
      })
      .catch(() => { /* unsupported or denied — stay null */ })
  } catch {
    /* never let device introspection break tracking */
  }
}

// Kick off immediately at module load: the promise usually resolves in
// microseconds (it's local data), so even the first page_view tends to carry
// the model. If it loses that race, the next event fills it — the backend
// writes device_model on the first payload that has one.
initDeviceModel()

// How long a first touch stays credited. Long enough to cover the research →
// buy gap (people price a trip, then buy nearer departure), short enough that
// a click from last quarter can't claim a sale. Measured from the ORIGINAL
// touch, not refreshed per visit — otherwise an active visitor's attribution
// would never age out.
const FIRST_TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000

// Exported so checkout can stamp the SAME session id onto the Order, letting
// paid orders join back to this session's first-touch acquisition source.
export function getSessionId(): string {
  if (typeof window === 'undefined') return ''
  let sid = sessionStorage.getItem(SESSION_KEY)
  if (!sid) {
    sid = generateSessionId()
    sessionStorage.setItem(SESSION_KEY, sid)
  }
  return sid
}

interface FirstTouch {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  fbclid?: string
  twclid?: string
  gclid?: string
  oppref?: string
  landing_path?: string
}

const ACQUISITION_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'fbclid', 'gclid', 'oppref', 'twclid',
] as const

/** The paid-click identifiers. Unlike the utm_* tags these are not a label for
 *  reporting — they are the only key an ad platform can use to match a
 *  conversion back to the click it paid for, and the server-side Conversions
 *  APIs require them explicitly. They are therefore merged forward even when
 *  first touch keeps the source label; see getFirstTouch. */
const CLICK_ID_KEYS = ['gclid', 'oppref', 'fbclid', 'twclid'] as const

/** Does this first touch actually say where the visitor came from? A record
 *  with only a landing_path doesn't — it's a direct visit. Used so a stored
 *  direct visit can't out-rank a later real ad click (see getFirstTouch). */
function hasAcquisitionSignal(ft: FirstTouch): boolean {
  return ACQUISITION_KEYS.some((k) => ft[k])
}

// The query string the acquisition tags actually live in.
//
// Normally that's just window.location.search. The exception is magic-link
// sign-in, which lands on
//   /auth/callback?access_token=…&next=%2Faccount%3Futm_source%3Demail%26…
// with the tags nested INSIDE ?next=, invisible at the top level. A page_view
// fires on that route, so first touch would snapshot an empty set — and since
// the backend writes the session_attribution row from the first event and
// never overwrites it, the later navigation to /account?utm_source=… can't
// repair it. Every account-holder's email click would be filed as "Direct".
//
// Only consulted when the top level carries no tags of its own, so a real
// landing URL always wins.
function acquisitionQuery(): URLSearchParams {
  const sp = new URLSearchParams(window.location.search)
  if (ACQUISITION_KEYS.some((k) => sp.get(k))) return sp
  const next = sp.get('next') // already percent-decoded by URLSearchParams
  const q = next ? next.indexOf('?') : -1
  return q === -1 ? sp : new URLSearchParams(next!.slice(q + 1))
}

/** Read the acquisition tags off the CURRENT url. */
function captureFirstTouch(): FirstTouch {
  const sp = acquisitionQuery()
  const ft: FirstTouch = {}
  const put = (k: 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term', v: string | null) => {
    if (v) ft[k] = v.slice(0, 200)
  }
  put('utm_source', sp.get('utm_source'))
  put('utm_medium', sp.get('utm_medium'))
  put('utm_campaign', sp.get('utm_campaign'))
  put('utm_content', sp.get('utm_content'))
  put('utm_term', sp.get('utm_term'))
  const fb = sp.get('fbclid')
  if (fb) ft.fbclid = fb.slice(0, 500)
  // Google Ads auto-tag. Captured for the same reason as fbclid — and as the
  // backstop for the paid-Google split, which otherwise relies entirely on
  // the campaign's final URL suffix being configured correctly.
  const gc = sp.get('gclid')
  if (gc) ft.gclid = gc.slice(0, 500)
  // OpenAI (ChatGPT Ads) click id — their gclid. Two reasons to keep our own
  // copy: it's the same backstop as above if the UTM suffix is ever dropped,
  // AND the server-side Conversions API requires it to be passed explicitly
  // (unlike the browser pixel, it does not read the cookie for you). The oaiq
  // pixel stores its own copy in __oppref; this one is ours.
  const op = sp.get('oppref')
  if (op) ft.oppref = op.slice(0, 500)
  // X Ads click id — same role as gclid/oppref for paid-X attribution.
  const tw = sp.get('twclid')
  if (tw) ft.twclid = tw.slice(0, 500)
  // pathname only (no query string) — the useful UTM bits are captured as
  // their own fields above; storing the full query risks persisting secrets.
  ft.landing_path = window.location.pathname.slice(0, 500)
  return ft
}

/** Stored shape. Versioned via the storage key; `ts` is the original capture
 *  time so the TTL measures age of the touch, not of the last visit. */
interface StoredFirstTouch {
  ts: number
  ft: FirstTouch
}

function readStoredFirstTouch(): FirstTouch | null {
  const raw = localStorage.getItem(FIRST_TOUCH_KEY)
  if (!raw) return null
  const parsed = JSON.parse(raw) as StoredFirstTouch
  if (!parsed?.ts || Date.now() - parsed.ts > FIRST_TOUCH_TTL_MS) {
    localStorage.removeItem(FIRST_TOUCH_KEY)
    return null
  }
  return parsed.ft ?? null
}

/** The original capture time of the stored touch, so a click-id merge can
 *  rewrite the record without resetting its TTL. Returns null if absent or
 *  unreadable — callers fall back to now. */
function storedFirstTouchTs(): number | null {
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as StoredFirstTouch
    return typeof parsed?.ts === 'number' ? parsed.ts : null
  } catch {
    return null
  }
}

// Acquisition tags from the landing URL, captured once and cached for
// FIRST_TOUCH_TTL_MS. We snapshot rather than read live because the SPA
// navigates away from the ad URL (and its ?utm_*/fbclid/gclid/oppref query)
// right after mount, so every later event still carries the original set.
//
// Storage is localStorage, NOT sessionStorage. sessionStorage dies with the
// TAB, which meant a visitor who clicked an ad, closed the tab, and came back
// days later to buy was filed as "Direct" — we'd paid for the click and
// credited nobody. eSIM buying is exactly that shape: people price a trip,
// then purchase nearer departure. (`session_id` above deliberately STAYS in
// sessionStorage — per-tab is the funnel semantics the admin dashboard counts.)
//
// First touch wins, with one exception: a stored record carrying no
// acquisition signal at all (a direct visit) must not out-rank a later real ad
// click, or a visitor who browsed directly on Monday would swallow the
// attribution for the ad they clicked on Thursday — the opposite of the fix.
function getFirstTouch(): FirstTouch {
  if (typeof window === 'undefined') return {}
  try {
    const cached = readStoredFirstTouch()
    const current = captureFirstTouch()
    if (cached && (hasAcquisitionSignal(cached) || !hasAcquisitionSignal(current))) {
      // First touch keeps the SOURCE label — that is the whole point of first
      // touch. But returning the cached record WHOLE also threw away any click
      // id on the current URL, and those are not a label: gclid/oppref are the
      // only keys that let a platform match this conversion to the click it
      // charged us for, and the server-side Conversions APIs require them
      // explicitly. The platforms dedupe on them themselves.
      //
      // The case this breaks is not exotic, it is the common one here: someone
      // finds us through an organic ChatGPT citation (utm_source=chatgpt.com,
      // no click id), which caches an acquisition signal for 30 days — then
      // later clicks a PAID ChatGPT ad. Without this merge that ad click is
      // invisible, and the spend reports as organic.
      const merged: FirstTouch = { ...cached }
      let changed = false
      for (const k of CLICK_ID_KEYS) {
        const v = current[k]
        if (v && merged[k] !== v) {
          merged[k] = v
          changed = true
        }
      }
      if (changed) {
        // Keep the ORIGINAL timestamp: the TTL measures the age of the touch,
        // and a later click id must not silently extend a stale attribution.
        const ts = storedFirstTouchTs() ?? Date.now()
        localStorage.setItem(
          FIRST_TOUCH_KEY,
          JSON.stringify({ ts, ft: merged } satisfies StoredFirstTouch),
        )
      }
      return merged
    }
    localStorage.setItem(
      FIRST_TOUCH_KEY,
      JSON.stringify({ ts: Date.now(), ft: current } satisfies StoredFirstTouch),
    )
    return current
  } catch {
    // Storage blocked (private mode / ITP) — fall back to reading the URL live.
    try {
      return captureFirstTouch()
    } catch {
      return {}
    }
  }
}

function generateSessionId(): string {
  // crypto.randomUUID() is available in all browsers we care about (Chrome 92+,
  // Safari 15.4+, Firefox 95+) — falls back to a Math.random hex if missing.
  try {
    return (crypto as Crypto & { randomUUID?: () => string }).randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

export type EventType =
  | 'page_view'
  | 'destination_view'
  | 'plan_clicked'
  | 'checkout_started'
  | 'checkout_email_entered'
  | 'coupon_applied'
  | 'referral_applied'
  | 'payment_attempted'
  | 'payment_succeeded'
  | 'payment_failed'
  // Pay clicked but the attempt died before Stripe (form validation or our
  // create-order call) — see the backend allow-list comment for the taxonomy.
  | 'attempt_error'
  | 'one_tap_install_clicked'
  // Landing-page engagement. Generic on purpose — metadata carries which page
  // and which element — so any page can emit them.
  | 'cta_clicked'
  | 'faq_opened'
  | 'compat_opened'
  // Real-user timing of checkout's payment form (Checkout.tsx PaymentSection).
  // Diagnostic only: not a funnel step, never forwarded to ad platforms.
  | 'checkout_payment_ready'

export interface TrackPayload {
  [key: string]: string | number | boolean | null | undefined
}

export function track(type: EventType, metadata?: TrackPayload): void {
  if (typeof window === 'undefined') return
  const body = {
    type,
    session_id: getSessionId(),
    // pathname only — never the query string. Some routes carry secrets in the
    // query (auth callback access_token, order-confirmation client secret) and
    // those must not land in the analytics table. UTMs are captured separately
    // via getFirstTouch().
    path: window.location.pathname,
    referrer: document.referrer || null,
    metadata: metadata ?? null,
    // Android model via Client Hints (null on iPhone/Safari/Firefox — see
    // initDeviceModel). Session-level fact; backend stores it once per session.
    device_model: deviceModel ?? undefined,
    // First-touch acquisition tags (undefined keys are dropped by JSON.stringify).
    ...getFirstTouch(),
  }
  // Fire-and-forget. We don't await; we also catch and silently drop so
  // a 4xx during dev (e.g., unknown event type) doesn't bubble to the user.
  apiFetch('/api/track', {
    method: 'POST',
    body: JSON.stringify(body),
  }).catch(() => {
    /* swallow */
  })
}